import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { useRefresh } from '../../../contexts/RefreshContext';
import ChangePasswordModal from '../../../components/ChangePasswordModal';
import ExportStatisticsModal from '../../../components/ExportStatisticsModal';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const { currentUser, logout, fetchUserDetails } = useAuth();
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);
  const [hasLoadedUserDetails, setHasLoadedUserDetails] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
  };

  const handleAccountsNavigation = () => {
    navigate('/dashboard/accounts');
  };

  const handleStatisticsNavigation = () => {
    navigate('/dashboard/statistics');
  };

  const handleMonthlyPlanNavigation = () => {
    navigate('/dashboard/monthly-plan');
  };

  const handleCategoriesNavigation = () => {
    navigate('/dashboard/profile/categories');
  };

  const handleRecurringNavigation = () => {
    navigate('/dashboard/recurring');
  };

  const handleGeminiNavigation = () => {
    navigate('/dashboard/gemini-ocr');
  };

  const handleRefresh = useCallback(async () => {
    await fetchUserDetails();
    setHasLoadedUserDetails(true);
  }, [fetchUserDetails]);

  useRefresh(handleRefresh);

  // Fetch fresh user details when component mounts
  useEffect(() => {
    let isMounted = true;
    
    const loadUserDetails = async () => {
      if (!isMounted || hasLoadedUserDetails || isLoadingUserDetails) return;
      
      try {
        setIsLoadingUserDetails(true);
        await fetchUserDetails();
        setHasLoadedUserDetails(true);
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      } finally {
        if (isMounted) {
          setIsLoadingUserDetails(false);
        }
      }
    };

    loadUserDetails();
    
    return () => {
      isMounted = false;
    };
  }, []); // Empty dependency array - only run once on mount

  const user = (currentUser as any)?.user || currentUser;

  return (
    <div className="profile-page">
      <div className="profile-cards">
        <div className="profile-section">
          <div className="options-grid">
            <div 
              className="profile-option-card"
              onClick={handleAccountsNavigation}
            >
              <div className="option-icon-wrapper accounts">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 18H4V8H20V18Z" fill="currentColor"/>
                  <path d="M6 10H18V12H6V10ZM6 14H14V16H6V14Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Рахунки</span>
                <span className="option-description">
                  Керування вашими фінансовими рахунками
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div 
              className="profile-option-card"
              onClick={handleStatisticsNavigation}
            >
              <div className="option-icon-wrapper statistics">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Статистика</span>
                <span className="option-description">
                  Аналіз витрат за категоріями та періодами
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div 
              className="profile-option-card"
              onClick={handleMonthlyPlanNavigation}
            >
              <div className="option-icon-wrapper monthly-plan">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
                  <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M7 14H11M7 18H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">План місяця</span>
                <span className="option-description">
                  Розподіл доходу та контроль вільних коштів по тижнях
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div 
              className="profile-option-card"
              onClick={handleCategoriesNavigation}
            >
              <div className="option-icon-wrapper categories">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Категорії</span>
                <span className="option-description">
                  Керування категоріями транзакцій
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div 
              className="profile-option-card"
              onClick={handleRecurringNavigation}
            >
              <div className="option-icon-wrapper recurring">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 6L18.29 8.29L13.41 13.17L9.41 9.17L2 16.59L3.41 18L9.41 12L13.41 16L19.71 9.71L22 12V6H16Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Шаблони витрат</span>
                <span className="option-description">
                  Керування повторюваними витратами
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div 
              className="profile-option-card"
              onClick={handleGeminiNavigation}
            >
              <div className="option-icon-wrapper gemini">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="currentColor"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Gemini OCR</span>
                <span className="option-description">
                  Обробка чеків через Google Gemini AI
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-section">
          <div className="options-grid">
            <div 
              className="profile-option-card"
              onClick={() => setIsExportModalOpen(true)}
            >
              <div className="option-icon-wrapper export">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="7 10 12 15 17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Експорт статистики</span>
                <span className="option-description">
                  Завантажити дані в CSV
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div 
              className="profile-option-card"
              onClick={() => setIsChangePasswordModalOpen(true)}
            >
              <div className="option-icon-wrapper password">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 3C13.66 3 15 4.34 15 6V8H9V6C9 4.34 10.34 3 12 3ZM18 20H6V10H18V20Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Змінити пароль</span>
                <span className="option-description">
                  Оновити пароль вашого акаунта
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            <div 
              className="profile-option-card logout-card"
              onClick={handleLogout}
            >
              <div className="option-icon-wrapper logout">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7Z" fill="currentColor"/>
                  <path d="M4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
                </svg>
              </div>
              <div className="option-content">
                <span className="option-label">Вийти з акаунта</span>
                <span className="option-description">
                  Завершити поточну сесію
                </span>
              </div>
              <div className="option-arrow">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
      />
      <ExportStatisticsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
    </div>
  );
};

export default ProfilePage;
